// src/app/scheduling/services/user-assign-schedules.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, EMPTY, from, of } from 'rxjs';
import {
  expand,
  map,
  mergeMap,
  reduce,
  shareReplay,
  toArray,
} from 'rxjs/operators';
import dayjs from 'dayjs';

import { environment } from '../../../environments/environment';
import {
  AssignedScheduleBlock,
  CreateUserAssignedScheduleRequest,
  DepartmentListItem,
  EmployeeWeekSchedules,
  ScheduleGroupDetails,
  ScheduleGroupListItem,
  ShiftListItem,
  YywwListItem,
} from '../models/user-assign-schedules.model';

interface ApiPagedResponse<T> {
  status: string;
  totalCount: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  data: T[];
}

interface ApiResponse<T> {
  status: string;
  data: T;
}

/* =============================
   Weekly report DTOs
   ============================= */

interface WeeklyReportUser {
  id: number;
  firstName: string;
  lastName: string;
  employeeNumber?: string | null;
  rfid?: string | null;
  reportToId?: number | null;
}

interface WeeklyReportScheduleItem {
  id: number;
  yyww: string;
  assignedDate: string;
  schedule: {
    id: number;
    localStartTime: string;
    localEndTime: string;
    timeZoneId: string;
    isOverNight: boolean;
    shift: {
      id: number;
      name: string;
    };
    department: {
      id: number;
      name: string;
      code?: number | null;
    };
  };
}

interface WeeklyReportUserSchedulesRow {
  user: WeeklyReportUser & {
    schedules: WeeklyReportScheduleItem[];
  };
}

interface WeeklyReportTimeLogsRow {
  user: WeeklyReportUser;
  logs: Array<{
    date: string;
    timeLogs: unknown[];
  }>;
}

interface WeeklyReportData {
  employeeCount: number;
  timeLogs: WeeklyReportTimeLogsRow[];
  bookedhours: unknown[];
  userSchedules: WeeklyReportUserSchedulesRow[];
}

interface WeeklyReportApiResponse {
  status: string;
  data: WeeklyReportData;
}

/* =============================
   /api/yywws
   ============================= */

interface YywwApiRow {
  id: number;
  yearWeek: string;
  fullYearWeek: string;
  year: number;
  week: number;
  startDate: string;
  endDate: string;
}

/* =============================
   /api/departments
   /api/shifts
   ============================= */

interface DepartmentApiRow {
  id: number;
  name: string;
  code?: number | null;
}

interface ShiftApiRow {
  id: number;
  name: string;
  gracePeriodMinutes?: number | null;
}

/* =============================
   /api/schedule-groups list
   ============================= */

interface ScheduleGroupDepartmentApiRow {
  id: number;
  name: string;
  code?: number | null;
  employeeCount?: number | null;
}

interface ScheduleGroupUserApiRow {
  id: number;
  firstName: string;
  lastName: string;
  employeeNumber?: string | null;
}

interface ScheduleGroupListApiRow {
  id: number;
  name: string;
  dayCount?: number | null;
  departments?: ScheduleGroupDepartmentApiRow[] | null;
  createdAt?: string | null;
  createdBy?: ScheduleGroupUserApiRow | null;
  isDeleted?: boolean;
  deletedAt?: string | null;
  deletedBy?: ScheduleGroupUserApiRow | null;
}

/* =============================
   /api/schedule-groups/{id}
   actual backend shape
   ============================= */

interface ScheduleGroupDetailsApiRow {
  id: number;
  name: string;
  createdAt?: string | null;
  createdBy?: ScheduleGroupUserApiRow | null;
  isDeleted?: boolean;
  deletedAt?: string | null;
  deletedBy?: ScheduleGroupUserApiRow | null;
  scheduleWithDays?: ScheduleGroupDayApiRow[] | null;
  departments?: ScheduleGroupDepartmentDetailsApiRow[] | null;
}

interface ScheduleGroupDayApiRow {
  id: number;
  dayofWeek:
    | 'Sunday'
    | 'Monday'
    | 'Tuesday'
    | 'Wednesday'
    | 'Thursday'
    | 'Friday'
    | 'Saturday';
  shiftSchedule?: {
    id: number;
    schedule?: {
      id: number;
      name: string;
      localStartTime: string;
      localEndTime: string;
      isOverNight: boolean;
      timeZoneId?: string | null;
    } | null;
    shift?: {
      id: number;
      name: string;
    } | null;
  } | null;
}

interface ScheduleGroupDepartmentDetailsApiRow {
  id: number;
  name: string;
  code?: number | null;
  employees?: Array<{
    id: number;
    firstName: string;
    lastName: string;
    employeeNumber?: string | null;
  }> | null;
}

type PagingStyle = 'pageNumber' | 'page';

@Injectable({ providedIn: 'root' })
export class UserAssignSchedulesService {
  private readonly baseUrl = environment.apiBaseUrl;
  private readonly PAGE_SIZE = 200;
  private readonly CREATE_CONCURRENCY = 4;

  private readonly weeksByYearCache = new Map<number, Observable<YywwListItem[]>>();
  private readonly weekSchedulesCache = new Map<string, Observable<EmployeeWeekSchedules[]>>();
  private readonly scheduleGroupDetailsCache = new Map<number, Observable<ScheduleGroupDetails>>();

  private departments$?: Observable<DepartmentListItem[]>;
  private shifts$?: Observable<ShiftListItem[]>;
  private scheduleGroups$?: Observable<ScheduleGroupListItem[]>;

  constructor(private readonly http: HttpClient) {}

  /* =============================
     Weeks
     ============================= */

  getWeeksByYear(year: number, forceRefresh = false): Observable<YywwListItem[]> {
    if (!forceRefresh) {
      const cached = this.weeksByYearCache.get(year);
      if (cached) return cached;
    }

    const params = new HttpParams().set('searchTerm', String(year));

    const request$ = this.fetchAllPages<YywwApiRow>(
      `${this.baseUrl}/api/yywws`,
      'pageNumber',
      params,
    ).pipe(
      map((rows) =>
        (rows ?? [])
          .map((r) => ({
            id: r.id,
            yearWeek: r.yearWeek,
            fullYearWeek: r.fullYearWeek,
            year: r.year,
            week: r.week,
            startDate: r.startDate,
            endDate: r.endDate,
          }))
          .sort((a, b) => (a.year !== b.year ? a.year - b.year : a.week - b.week)),
      ),
      shareReplay(1),
    );

    this.weeksByYearCache.set(year, request$);
    return request$;
  }

  findWeekContainingDate(weeks: YywwListItem[], date: Date): YywwListItem | null {
    const d = dayjs(date);

    for (const w of weeks ?? []) {
      const start = dayjs(w.startDate, 'YYYY-MM-DD', true);
      const end = dayjs(w.endDate, 'YYYY-MM-DD', true);

      if (!start.isValid() || !end.isValid()) continue;

      if (
        (d.isSame(start, 'day') || d.isAfter(start)) &&
        (d.isSame(end, 'day') || d.isBefore(end))
      ) {
        return w;
      }
    }

    return null;
  }

  /* =============================
     Week schedules
     ============================= */

  getWeekSchedulesByWeek(
    week: YywwListItem,
    forceRefresh = false,
  ): Observable<EmployeeWeekSchedules[]> {
    const key = week.fullYearWeek;
    if (!key) return of([]);

    if (!forceRefresh) {
      const cached = this.weekSchedulesCache.get(key);
      if (cached) return cached;
    }

    const params = new HttpParams().set('FullYearWeek', key);

    const request$ = this.http
      .get<WeeklyReportApiResponse>(`${this.baseUrl}/api/employee-time-logs/weekly-report`, {
        params,
      })
      .pipe(
        map((res) => this.mapWeeklyReportToWeekSchedules(res?.data)),
        shareReplay(1),
      );

    this.weekSchedulesCache.set(key, request$);
    return request$;
  }

  refreshWeekSchedules(week: YywwListItem): Observable<EmployeeWeekSchedules[]> {
    this.weekSchedulesCache.delete(week.fullYearWeek);
    return this.getWeekSchedulesByWeek(week, true);
  }

  clearWeekSchedulesCache(fullYearWeek?: string): void {
    if (fullYearWeek) {
      this.weekSchedulesCache.delete(fullYearWeek);
      return;
    }

    this.weekSchedulesCache.clear();
  }

  private mapWeeklyReportToWeekSchedules(data: WeeklyReportData | undefined): EmployeeWeekSchedules[] {
    const employeesById = new Map<number, WeeklyReportUser>();
    const schedulesByEmpId = new Map<number, AssignedScheduleBlock[]>();

    for (const row of data?.timeLogs ?? []) {
      const user = row?.user;
      if (user?.id) employeesById.set(user.id, user);
    }

    for (const row of data?.userSchedules ?? []) {
      const user = row?.user;
      if (!user?.id) continue;

      employeesById.set(user.id, user);

      const blocks = (user.schedules ?? [])
        .map((item) => this.mapWeeklyReportScheduleItem(user.id, item))
        .filter((b): b is AssignedScheduleBlock => !!b)
        .sort((a, b) => dayjs(a.startDate).valueOf() - dayjs(b.startDate).valueOf());

      schedulesByEmpId.set(user.id, blocks);
    }

    return Array.from(employeesById.values())
      .map((user) => ({
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        email: null,
        schedules: schedulesByEmpId.get(user.id) ?? [],
      }))
      .sort((a, b) => {
        const an = `${a.lastName ?? ''} ${a.firstName ?? ''}`.trim().toLowerCase();
        const bn = `${b.lastName ?? ''} ${b.firstName ?? ''}`.trim().toLowerCase();
        return an.localeCompare(bn);
      });
  }

  private mapWeeklyReportScheduleItem(
    employeeId: number,
    item: WeeklyReportScheduleItem,
  ): AssignedScheduleBlock | null {
    if (
      !item?.id ||
      !employeeId ||
      !item.assignedDate ||
      !item.schedule?.localStartTime ||
      !item.schedule?.localEndTime
    ) {
      return null;
    }

    const assignedDate = item.assignedDate;
    const start = this.buildLocalDateTime(assignedDate, item.schedule.localStartTime);
    let end = this.buildLocalDateTime(assignedDate, item.schedule.localEndTime);

    if (
      item.schedule.isOverNight ||
      dayjs(end).isSame(dayjs(start)) ||
      dayjs(end).isBefore(dayjs(start))
    ) {
      end = dayjs(end).add(1, 'day').toDate();
    }

    const dept = item.schedule.department;
    const shift = item.schedule.shift;

    return {
      assignmentId: item.id,
      employeeId,
      assignedDateYmd: assignedDate,
      startDate: start,
      endDate: end,
      isOvernight: dayjs(end).startOf('day').isAfter(dayjs(start).startOf('day')),
      departmentId: dept?.id ?? null,
      shiftId: shift?.id ?? null,
      departmentName: dept?.name ?? null,
      departmentCode: dept?.code ?? null,
      shiftName: shift?.name ?? null,
      timeZoneId: item.schedule.timeZoneId ?? null,
      scheduleName: null,
    };
  }

  /* =============================
     Create / Delete
     ============================= */

  createAssignedSchedule(body: CreateUserAssignedScheduleRequest): Observable<ApiResponse<unknown>> {
    return this.http.post<ApiResponse<unknown>>(
      `${this.baseUrl}/api/user-assigned-schedules`,
      body,
    );
  }

  createAssignedSchedules(
    bodies: CreateUserAssignedScheduleRequest[],
  ): Observable<ApiResponse<unknown>[]> {
    const list = (bodies ?? []).filter(Boolean);
    if (!list.length) return of([]);

    return from(list).pipe(
      mergeMap((body) => this.createAssignedSchedule(body), this.CREATE_CONCURRENCY),
      toArray(),
    );
  }

  deleteAssignedSchedule(id: number): Observable<ApiResponse<unknown>> {
    return this.http.delete<ApiResponse<unknown>>(
      `${this.baseUrl}/api/user-assigned-schedules/${id}`,
    );
  }

  /* =============================
     Supporting data
     ============================= */

  getAllDepartments(forceRefresh = false): Observable<DepartmentListItem[]> {
    if (!forceRefresh && this.departments$) return this.departments$;

    this.departments$ = this.fetchAllPages<DepartmentApiRow>(
      `${this.baseUrl}/api/departments`,
      'page',
    ).pipe(
      map((rows) =>
        (rows ?? [])
          .map((d) => ({
            id: d.id,
            name: d.name,
            code: d.code ?? null,
          }))
          .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')),
      ),
      shareReplay(1),
    );

    return this.departments$;
  }

  getAllShifts(forceRefresh = false): Observable<ShiftListItem[]> {
    if (!forceRefresh && this.shifts$) return this.shifts$;

    this.shifts$ = this.fetchAllPages<ShiftApiRow>(`${this.baseUrl}/api/shifts`, 'page').pipe(
      map((rows) =>
        (rows ?? [])
          .map((s) => ({
            id: s.id,
            name: s.name,
            gracePeriodMinutes: s.gracePeriodMinutes ?? null,
          }))
          .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')),
      ),
      shareReplay(1),
    );

    return this.shifts$;
  }

  /* =============================
     Schedule groups
     ============================= */

  getScheduleGroupsAll(forceRefresh = false): Observable<ScheduleGroupListItem[]> {
    if (!forceRefresh && this.scheduleGroups$) return this.scheduleGroups$;

    this.scheduleGroups$ = this.fetchAllPages<ScheduleGroupListApiRow>(
      `${this.baseUrl}/api/schedule-groups`,
      'pageNumber',
    ).pipe(
      map((rows) =>
        (rows ?? [])
          .filter((g) => !g?.isDeleted)
          .map((g) => ({
            id: g.id,
            name: g.name,
            dayCount: Number(g.dayCount ?? 0),
            departments: (g.departments ?? []).map((d) => ({
              id: d.id,
              name: d.name,
              code: d.code ?? null,
              employeeCount: d.employeeCount ?? 0,
            })),
            createdAt: g.createdAt ?? null,
            createdBy: g.createdBy
              ? {
                  id: g.createdBy.id,
                  firstName: g.createdBy.firstName,
                  lastName: g.createdBy.lastName,
                  employeeNumber: g.createdBy.employeeNumber ?? null,
                }
              : null,
            isDeleted: !!g.isDeleted,
            deletedAt: g.deletedAt ?? null,
            deletedBy: g.deletedBy
              ? {
                  id: g.deletedBy.id,
                  firstName: g.deletedBy.firstName,
                  lastName: g.deletedBy.lastName,
                  employeeNumber: g.deletedBy.employeeNumber ?? null,
                }
              : null,
          }))
          .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')),
      ),
      shareReplay(1),
    );

    return this.scheduleGroups$;
  }

  getScheduleGroupById(id: number, forceRefresh = false): Observable<ScheduleGroupDetails> {
    if (!forceRefresh) {
      const cached = this.scheduleGroupDetailsCache.get(id);
      if (cached) return cached;
    }

    const request$ = this.http
      .get<ApiResponse<ScheduleGroupDetailsApiRow>>(`${this.baseUrl}/api/schedule-groups/${id}`)
      .pipe(
        map((res) => this.mapScheduleGroupDetails(res.data)),
        shareReplay(1),
      );

    this.scheduleGroupDetailsCache.set(id, request$);
    return request$;
  }

  clearScheduleGroupsCache(): void {
    this.scheduleGroups$ = undefined;
    this.scheduleGroupDetailsCache.clear();
  }

  private mapScheduleGroupDetails(data: ScheduleGroupDetailsApiRow): ScheduleGroupDetails {
    return {
      id: data.id,
      name: data.name,
      createdAt: data.createdAt ?? null,
      createdBy: data.createdBy
        ? {
            id: data.createdBy.id,
            firstName: data.createdBy.firstName,
            lastName: data.createdBy.lastName,
            employeeNumber: data.createdBy.employeeNumber ?? null,
          }
        : null,
      isDeleted: !!data.isDeleted,
      deletedAt: data.deletedAt ?? null,
      deletedBy: data.deletedBy
        ? {
            id: data.deletedBy.id,
            firstName: data.deletedBy.firstName,
            lastName: data.deletedBy.lastName,
            employeeNumber: data.deletedBy.employeeNumber ?? null,
          }
        : null,
      scheduleWithDays: (data.scheduleWithDays ?? []).map((row) => ({
        id: row.id,
        dayofWeek: row.dayofWeek,
        shiftSchedule: {
          id: row.shiftSchedule?.id ?? 0,
          schedule: {
            id: row.shiftSchedule?.schedule?.id ?? 0,
            name: row.shiftSchedule?.schedule?.name ?? '',
            localStartTime: row.shiftSchedule?.schedule?.localStartTime ?? '',
            localEndTime: row.shiftSchedule?.schedule?.localEndTime ?? '',
            isOverNight: !!row.shiftSchedule?.schedule?.isOverNight,
            timeZoneId: row.shiftSchedule?.schedule?.timeZoneId ?? null,
          },
          shift: {
            id: row.shiftSchedule?.shift?.id ?? 0,
            name: row.shiftSchedule?.shift?.name ?? '',
          },
        },
      })),
      departments: (data.departments ?? []).map((dept) => ({
        id: dept.id,
        name: dept.name,
        code: dept.code ?? null,
        employees: (dept.employees ?? []).map((emp) => ({
          id: emp.id,
          firstName: emp.firstName,
          lastName: emp.lastName,
          employeeNumber: emp.employeeNumber ?? null,
        })),
      })),
    };
  }

  /* =============================
     Helpers
     ============================= */

  private buildLocalDateTime(dateYmd: string, timeHms: string): Date {
    const t = String(timeHms ?? '').trim();
    const hhmm = t.length >= 5 ? t.slice(0, 5) : t;
    return new Date(`${dateYmd}T${hhmm}:00`);
  }

  /* =============================
     Pagination
     ============================= */

  private fetchAllPages<T>(
    url: string,
    paging: PagingStyle,
    baseParams?: HttpParams,
  ): Observable<T[]> {
    const getPage = (pageNumber: number) => {
      const params = this.withPagingParams(
        baseParams ?? new HttpParams(),
        paging,
        pageNumber,
        this.PAGE_SIZE,
      );
      return this.http.get<ApiPagedResponse<T>>(url, { params });
    };

    return getPage(1).pipe(
      expand((res) => {
        const current = res?.currentPage ?? 1;
        const total = res?.totalPages ?? 1;
        const next = current + 1;
        return next > total ? EMPTY : getPage(next);
      }),
      map((res) => res?.data ?? []),
      reduce((all, pageData) => all.concat(pageData), [] as T[]),
    );
  }

  private withPagingParams(
    params: HttpParams,
    paging: PagingStyle,
    pageNumber: number,
    pageSize: number,
  ): HttpParams {
    if (paging === 'pageNumber') {
      return params.set('pageNumber', String(pageNumber)).set('pageSize', String(pageSize));
    }

    return params.set('page', String(pageNumber)).set('pageSize', String(pageSize));
  }
}